import React, { useCallback, useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap"
import axios from "axios"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import withAuth from "../withAuth"
import { baseUrl } from "../../Network"
import { handleAuthError } from "../../utils"
import { useConfirm } from "../../contexts/ConfirmContext"
import { isMarketerReadOnly } from "../../helpers/adminRole"
import TestSettingsFields, { emptyTestSettings, settingsForSave, settingsFromTest } from "./TestSettingsFields"
import {
  QUESTION_TYPES,
  TestCardPreview,
  apiErrorMessage,
  downloadJson,
  exportShape,
  formatDateTime,
  lessonsFromCurriculum,
  questionPayload,
  slugifyFile,
  testPayload,
  useTestFormOptions,
  typeLabel,
} from "./aiTestShared"

const LETTERS = ["a", "b", "c", "d", "e", "f"]
const monospace = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" }
/** The theme draws unchecked radios almost white on white; give them a visible border. */
const visibleRadio = { borderColor: "#9ca3af", width: "1.15em", height: "1.15em", cursor: "pointer", flexShrink: 0 }

/** Editable form state for one question; turned into the API shape by draftToQuestion. */
const newDraft = (type) => ({
  type,
  prompt: type === "code_prediction" ? "What do you think this code will do?" : type === "match_pairs" ? "Match each piece of code to what it does" : type === "fill_blank" ? "Fill in the blank" : "",
  code: "",
  explanation: "",
  points: 1,
  lessonRef: null,
  options: ["", "", "", ""],
  answerIndex: 0,
  tfAnswer: true,
  pre: "",
  post: "",
  chips: ["", "", ""],
  chipAnswerIndex: 0,
  pairs: [
    { left: "", right: "" },
    { left: "", right: "" },
    { left: "", right: "" },
  ],
})

const draftFromQuestion = (q) => {
  const d = { ...newDraft(q.type), prompt: q.prompt || "", code: q.code || "", explanation: q.explanation || "", points: q.points || 1, lessonRef: q.lessonRef || null }
  const p = q.payload || {}
  if (q.type === "code_prediction" || q.type === "multiple_choice") {
    const opts = Array.isArray(p.options) ? p.options : []
    d.options = opts.map((o) => o.label || "")
    d.answerIndex = Math.max(0, opts.findIndex((o) => String(o.id) === String(p.answer)))
  } else if (q.type === "true_false") {
    d.tfAnswer = p.answer === true
  } else if (q.type === "fill_blank") {
    d.pre = p.pre || ""
    d.post = p.post || ""
    d.chips = Array.isArray(p.options) ? p.options.slice() : ["", ""]
    d.chipAnswerIndex = Math.max(0, d.chips.indexOf(p.answer))
  } else if (q.type === "match_pairs") {
    d.pairs = (Array.isArray(p.pairs) ? p.pairs : []).map((pair) => ({ left: pair.left || "", right: pair.right || "" }))
  }
  return d
}

const draftToQuestion = (d) => {
  let payload = {}
  if (d.type === "code_prediction" || d.type === "multiple_choice") {
    payload = {
      options: d.options.map((label, i) => ({ id: LETTERS[i], label: label.trim() })),
      answer: LETTERS[d.answerIndex] || "a",
    }
  } else if (d.type === "true_false") {
    payload = { answer: !!d.tfAnswer }
  } else if (d.type === "fill_blank") {
    payload = { pre: d.pre, post: d.post, options: d.chips.map((c) => c.trim()), answer: (d.chips[d.chipAnswerIndex] || "").trim() }
  } else if (d.type === "match_pairs") {
    payload = { pairs: d.pairs.map((pair, i) => ({ id: `p${i + 1}`, left: pair.left.trim(), right: pair.right.trim() })) }
  }
  return {
    type: d.type,
    prompt: d.prompt.trim(),
    code: d.type === "code_prediction" ? d.code : "",
    payload,
    explanation: d.explanation.trim(),
    points: Number(d.points) || 1,
    lessonRef: d.lessonRef && d.lessonRef.courseSlug ? d.lessonRef : null,
  }
}

/** Friendly checks before the API's own validation. Returns an error message or null. */
const draftProblem = (d) => {
  if (!d.prompt.trim()) return d.type === "true_false" ? "Write the statement" : "Write the question"
  if (d.type === "code_prediction" && !d.code.trim()) return "Add the code the learner will read"
  if (d.type === "code_prediction" || d.type === "multiple_choice") {
    if (d.options.length < 2) return "Add at least two options"
    if (d.options.some((o) => !o.trim())) return "Fill in every option, or remove the empty ones"
  }
  if (d.type === "fill_blank") {
    if (d.chips.length < 2) return "Add at least two word chips"
    const trimmed = d.chips.map((c) => c.trim())
    if (trimmed.some((c) => !c)) return "Fill in every word chip, or remove the empty ones"
    if (new Set(trimmed).size !== trimmed.length) return "Word chips must be different from each other"
  }
  if (d.type === "match_pairs") {
    if (d.pairs.length < 2) return "Add at least two pairs"
    if (d.pairs.some((p) => !p.left.trim() || !p.right.trim())) return "Fill in both sides of every pair"
  }
  return null
}

const QuestionFields = ({ draft, setDraft }) => {
  const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }))
  const setListItem = (key, i, value) =>
    setDraft((d) => {
      const list = d[key].slice()
      list[i] = value
      return { ...d, [key]: list }
    })
  const removeListItem = (key, indexKey, i) =>
    setDraft((d) => {
      const list = d[key].filter((_, j) => j !== i)
      const next = { ...d, [key]: list }
      if (indexKey) {
        if (d[indexKey] === i) next[indexKey] = 0
        else if (d[indexKey] > i) next[indexKey] = d[indexKey] - 1
      }
      return next
    })

  if (draft.type === "code_prediction" || draft.type === "multiple_choice") {
    return (
      <>
        {draft.type === "code_prediction" && (
          <FormGroup>
            <Label>Code</Label>
            <Input type="textarea" rows={5} style={monospace} value={draft.code} onChange={set("code")} placeholder={'print("Hello, World!")'} />
          </FormGroup>
        )}
        <Label>Options (tick the correct one)</Label>
        {draft.options.map((opt, i) => (
          <div key={i} className="d-flex align-items-center gap-2 mb-2">
            <Input
              type="radio"
              name="ai-test-correct-option"
              style={visibleRadio}
              checked={draft.answerIndex === i}
              onChange={() => setDraft((d) => ({ ...d, answerIndex: i }))}
              aria-label={`Option ${LETTERS[i].toUpperCase()} is correct`}
            />
            <strong style={{ width: 16 }}>{LETTERS[i].toUpperCase()}</strong>
            <Input value={opt} onChange={(e) => setListItem("options", i, e.target.value)} placeholder={`Option ${LETTERS[i].toUpperCase()}`} maxLength={500} />
            <Button size="sm" color="link" className="text-danger" disabled={draft.options.length <= 2} onClick={() => removeListItem("options", "answerIndex", i)}>
              Remove
            </Button>
          </div>
        ))}
        <Button size="sm" color="light" disabled={draft.options.length >= 6} onClick={() => setDraft((d) => ({ ...d, options: [...d.options, ""] }))}>
          Add option
        </Button>
      </>
    )
  }

  if (draft.type === "true_false") {
    return (
      <FormGroup tag="fieldset">
        <Label>The statement is…</Label>
        <div className="d-flex gap-4">
          {[true, false].map((v) => (
            <FormGroup check key={String(v)}>
              <Input type="radio" name="ai-test-tf" id={`ai-test-tf-${v}`} style={visibleRadio} checked={draft.tfAnswer === v} onChange={() => setDraft((d) => ({ ...d, tfAnswer: v }))} />
              <Label check for={`ai-test-tf-${v}`}>
                {v ? "True" : "False"}
              </Label>
            </FormGroup>
          ))}
        </div>
      </FormGroup>
    )
  }

  if (draft.type === "fill_blank") {
    return (
      <>
        <Row>
          <Col md={6}>
            <FormGroup>
              <Label>Code before the blank</Label>
              <Input style={monospace} value={draft.pre} onChange={set("pre")} placeholder="print(" />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label>Code after the blank</Label>
              <Input style={monospace} value={draft.post} onChange={set("post")} placeholder=")" />
            </FormGroup>
          </Col>
        </Row>
        <Label>Word chips (tick the one that fills the blank)</Label>
        {draft.chips.map((chip, i) => (
          <div key={i} className="d-flex align-items-center gap-2 mb-2">
            <Input
              type="radio"
              name="ai-test-correct-chip"
              style={visibleRadio}
              checked={draft.chipAnswerIndex === i}
              onChange={() => setDraft((d) => ({ ...d, chipAnswerIndex: i }))}
              aria-label={`Chip ${i + 1} is correct`}
            />
            <Input style={monospace} value={chip} onChange={(e) => setListItem("chips", i, e.target.value)} placeholder={`Chip ${i + 1}`} maxLength={200} />
            <Button size="sm" color="link" className="text-danger" disabled={draft.chips.length <= 2} onClick={() => removeListItem("chips", "chipAnswerIndex", i)}>
              Remove
            </Button>
          </div>
        ))}
        <Button size="sm" color="light" disabled={draft.chips.length >= 8} onClick={() => setDraft((d) => ({ ...d, chips: [...d.chips, ""] }))}>
          Add chip
        </Button>
      </>
    )
  }

  if (draft.type === "match_pairs") {
    return (
      <>
        <Label>Pairs (the descriptions are shuffled for the learner)</Label>
        {draft.pairs.map((pair, i) => (
          <Row key={i} className="g-2 mb-2 align-items-center">
            <Col md={5}>
              <Input
                style={monospace}
                value={pair.left}
                onChange={(e) => setListItem("pairs", i, { ...pair, left: e.target.value })}
                placeholder={'Code, e.g. print("hi")'}
                maxLength={500}
              />
            </Col>
            <Col md={5}>
              <Input
                value={pair.right}
                onChange={(e) => setListItem("pairs", i, { ...pair, right: e.target.value })}
                placeholder="What it does"
                maxLength={500}
              />
            </Col>
            <Col md={2}>
              <Button size="sm" color="link" className="text-danger" disabled={draft.pairs.length <= 2} onClick={() => removeListItem("pairs", null, i)}>
                Remove
              </Button>
            </Col>
          </Row>
        ))}
        <Button size="sm" color="light" disabled={draft.pairs.length >= 6} onClick={() => setDraft((d) => ({ ...d, pairs: [...d.pairs, { left: "", right: "" }] }))}>
          Add pair
        </Button>
      </>
    )
  }
  return null
}

/** Optional tag that points a card at a lesson, used by "review the lessons you missed". */
const LessonTagFields = ({ draft, setDraft, curricula, defaultCurriculumId }) => {
  const slugToId = (slug) => curricula.find((c) => c.slug === slug)?.id
  const [courseId, setCourseId] = useState(() => (draft.lessonRef?.courseSlug ? slugToId(draft.lessonRef.courseSlug) || "" : defaultCurriculumId || ""))
  const [lessons, setLessons] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!courseId) {
      setLessons([])
      return undefined
    }
    let cancelled = false
    setLoading(true)
    axios
      .get(`${baseUrl}/admin/curriculum/${courseId}`)
      .then((res) => {
        if (!cancelled) setLessons(lessonsFromCurriculum(res?.data?.data))
      })
      .catch((e) => {
        handleAuthError(e)
        if (!cancelled) setLessons([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [courseId])

  const course = curricula.find((c) => String(c.id) === String(courseId))
  const pickLesson = (lessonId) => {
    if (!lessonId || !course) {
      setDraft((d) => ({ ...d, lessonRef: course ? { courseSlug: course.slug, lessonId: null, lessonTitle: null } : null }))
      return
    }
    const lesson = lessons.find((l) => l.id === lessonId)
    setDraft((d) => ({ ...d, lessonRef: { courseSlug: course.slug, lessonId, lessonTitle: lesson?.title || null } }))
  }

  return (
    <Row className="g-2">
      <Col md={6}>
        <Label className="small mb-1">Course</Label>
        <Input
          bsSize="sm"
          type="select"
          value={courseId}
          onChange={(e) => {
            setCourseId(e.target.value)
            const c = curricula.find((x) => String(x.id) === e.target.value)
            setDraft((d) => ({ ...d, lessonRef: c ? { courseSlug: c.slug, lessonId: null, lessonTitle: null } : null }))
          }}
        >
          <option value="">No lesson tag</option>
          {curricula.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </Input>
      </Col>
      <Col md={6}>
        <Label className="small mb-1">Lesson {loading && <Spinner size="sm" />}</Label>
        <Input bsSize="sm" type="select" value={draft.lessonRef?.lessonId || ""} onChange={(e) => pickLesson(e.target.value)} disabled={!courseId || loading}>
          <option value="">Whole course</option>
          {lessons.map((l) => (
            <option key={l.id} value={l.id}>
              {l.moduleTitle} — {l.title}
            </option>
          ))}
        </Input>
      </Col>
    </Row>
  )
}

const ManageAiTestEditor = () => {
  const { id } = useParams()
  const { showConfirm } = useConfirm()
  const readOnly = isMarketerReadOnly()
  const [test, setTest] = useState(null)
  const [settings, setSettings] = useState(emptyTestSettings)
  const { curricula, categories, tests } = useTestFormOptions()
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [savingSettings, setSavingSettings] = useState(false)
  /** Action in progress: "publish", "move:<id>:up|down" or "delete:<id>"; that button shows a spinner. */
  const [busy, setBusy] = useState(null)
  const [modal, setModal] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [draft, setDraft] = useState(newDraft("code_prediction"))
  const [savingQuestion, setSavingQuestion] = useState(false)
  const [modalError, setModalError] = useState("")

  document.title = `${test?.title || "Test"} | RYD Admin`

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/ai-test/${id}`)
      if (res?.data?.status) {
        setTest(res.data.data)
        setSettings(settingsFromTest(res.data.data))
      }
    } catch (e) {
      handleAuthError(e)
      if (e?.response?.status === 404) setNotFound(true)
      else toast.error(apiErrorMessage(e, "Could not load the test"))
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  const questions = test?.questions || []

  const saveSettings = async () => {
    if (!String(settings.title).trim()) {
      toast.error("Give the test a title")
      return
    }
    try {
      setSavingSettings(true)
      const res = await axios.put(`${baseUrl}/admin/ai-test/${id}`, testPayload(settingsForSave(settings)))
      if (!res?.data?.status) {
        toast.error(res?.data?.message || "Could not save")
        return
      }
      toast.success("Settings saved")
      await load()
    } catch (e) {
      handleAuthError(e)
      toast.error(apiErrorMessage(e, "Could not save"))
    } finally {
      setSavingSettings(false)
    }
  }

  const togglePublished = async () => {
    if (busy) return
    try {
      setBusy("publish")
      const res = await axios.put(`${baseUrl}/admin/ai-test/publish/${id}`, { isPublished: !test.isPublished })
      if (!res?.data?.status) {
        toast.error(res?.data?.message || "Could not change publishing")
        return
      }
      toast.success(res.data.message)
      await load()
    } catch (e) {
      handleAuthError(e)
      toast.error(apiErrorMessage(e, "Could not change publishing"))
    } finally {
      setBusy(null)
    }
  }

  const exportJson = () => downloadJson(exportShape(test), `ai-test-${slugifyFile(test.title)}.json`)

  const openAdd = (type = "code_prediction") => {
    setEditingId(null)
    setDraft(newDraft(type))
    setModalError("")
    setModal(true)
  }

  const openEdit = (q) => {
    setEditingId(q.id)
    setDraft(draftFromQuestion(q))
    setModalError("")
    setModal(true)
  }

  const changeType = (type) => {
    // Keep what carries over between types; reset the type-specific parts.
    setDraft((d) => ({ ...newDraft(type), explanation: d.explanation, points: d.points, lessonRef: d.lessonRef, prompt: d.prompt && d.prompt !== newDraft(d.type).prompt ? d.prompt : newDraft(type).prompt }))
  }

  const saveQuestion = async () => {
    const problem = draftProblem(draft)
    if (problem) {
      setModalError(problem)
      return
    }
    try {
      setSavingQuestion(true)
      setModalError("")
      const body = questionPayload(draftToQuestion(draft))
      const res = editingId
        ? await axios.put(`${baseUrl}/admin/ai-test/question/${editingId}`, body)
        : await axios.post(`${baseUrl}/admin/ai-test/${id}/question`, body)
      if (!res?.data?.status) {
        setModalError(res?.data?.message || "Could not save the question")
        return
      }
      toast.success(editingId ? "Question updated" : "Question added")
      setModal(false)
      await load()
    } catch (e) {
      handleAuthError(e)
      setModalError(apiErrorMessage(e, "Could not save the question"))
    } finally {
      setSavingQuestion(false)
    }
  }

  const removeQuestion = async (q, index) => {
    const ok = await showConfirm({
      title: "Delete question",
      message: `Delete question ${index + 1}?${test.isPublished && questions.length === 1 ? " This is the last question of a published test." : ""}`,
      variant: "danger",
    })
    if (!ok) return
    try {
      setBusy(`delete:${q.id}`)
      await axios.delete(`${baseUrl}/admin/ai-test/question/${q.id}`)
      toast.success("Question deleted")
      await load()
    } catch (e) {
      handleAuthError(e)
      toast.error(apiErrorMessage(e, "Could not delete the question"))
    } finally {
      setBusy(null)
    }
  }

  const move = async (index, delta) => {
    const target = index + delta
    if (target < 0 || target >= questions.length) return
    const ids = questions.map((q) => q.id)
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    if (busy) return
    try {
      setBusy(`move:${questions[index].id}:${delta < 0 ? "up" : "down"}`)
      const res = await axios.put(`${baseUrl}/admin/ai-test/${id}/question-order`, { ids })
      if (res?.data?.status) setTest((t) => ({ ...t, questions: res.data.data }))
    } catch (e) {
      handleAuthError(e)
      toast.error(apiErrorMessage(e, "Could not reorder"))
    } finally {
      setBusy(null)
    }
  }

  if (loading) {
    return (
      <div className="page-content">
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      </div>
    )
  }

  if (notFound || !test) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="warning">
            This test was not found. <Link to="/ai/tests">Back to tests</Link>
          </Alert>
        </Container>
        <ToastContainer />
      </div>
    )
  }

  const previewQuestion = draftToQuestion(draft)

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Interactive tests" breadcrumbItem={test.title} />
          <div className="d-flex flex-wrap gap-2 justify-content-end align-items-center mb-3">
            <Button tag={Link} to="/ai/tests" color="light" size="sm">
              All tests
            </Button>
            <Button tag={Link} to={`/ai/test-attempts?testId=${test.id}`} color="light" size="sm">
              Attempts
            </Button>
            <Button color="secondary" outline size="sm" onClick={exportJson}>
              Export JSON
            </Button>
            {!readOnly && (
              <Button color={test.isPublished ? "warning" : "success"} size="sm" onClick={togglePublished} disabled={!!busy}>
                {busy === "publish" ? (
                  <>
                    <Spinner size="sm" className="me-1" /> {test.isPublished ? "Unpublishing…" : "Publishing…"}
                  </>
                ) : test.isPublished ? (
                  "Unpublish"
                ) : (
                  "Publish"
                )}
              </Button>
            )}
          </div>

          <Row>
            <Col xl={5}>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Settings</h5>
                  <div>
                    <Badge color={test.isPublished ? "success" : "secondary"}>{test.isPublished ? "Published" : "Draft"}</Badge>
                  </div>
                </CardHeader>
                <CardBody>
                  <TestSettingsFields
                    form={settings}
                    setForm={setSettings}
                    curricula={curricula}
                    categories={categories}
                    tests={tests}
                    testId={test?.id}
                    disabled={readOnly}
                  />
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <small className="text-muted">Last modified {formatDateTime(test.updatedAt)}</small>
                    {!readOnly && (
                      <Button color="primary" size="sm" onClick={saveSettings} disabled={savingSettings}>
                        {savingSettings ? <Spinner size="sm" /> : "Save settings"}
                      </Button>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={7}>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h5 className="mb-0">
                    Questions <span className="text-muted">({questions.length})</span>
                  </h5>
                  {!readOnly && (
                    <div className="d-flex gap-1 flex-wrap">
                      {QUESTION_TYPES.map((t) => (
                        <Button key={t.value} size="sm" color="primary" outline onClick={() => openAdd(t.value)}>
                          + {t.label}
                        </Button>
                      ))}
                    </div>
                  )}
                </CardHeader>
                <CardBody>
                  {!questions.length && (
                    <div className="text-center text-muted py-4">No questions yet. Add one with the buttons above. A test needs at least one question to be published.</div>
                  )}
                  {questions.map((q, i) => (
                    <div key={q.id} className="border rounded p-3 mb-2 d-flex gap-3 align-items-start">
                      <div className="fw-bold text-muted" style={{ minWidth: 24 }}>
                        {i + 1}.
                      </div>
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="d-flex gap-2 flex-wrap mb-1">
                          <Badge color="info">{typeLabel(q.type)}</Badge>
                          <Badge color="light" className="text-dark">
                            {q.points} pt{q.points === 1 ? "" : "s"}
                          </Badge>
                          {q.lessonRef?.courseSlug && (
                            <Badge color="light" className="text-dark" title="Missed cards point the learner here">
                              Lesson: {q.lessonRef.lessonTitle || q.lessonRef.lessonId || q.lessonRef.courseSlug}
                            </Badge>
                          )}
                        </div>
                        <div className="fw-semibold text-truncate">{q.prompt}</div>
                        {q.code && (
                          <div className="small text-muted text-truncate" style={monospace}>
                            {q.code.split("\n")[0]}
                          </div>
                        )}
                      </div>
                      {!readOnly && (
                        <div className="d-flex gap-1 flex-shrink-0">
                          <Button size="sm" color="light" disabled={!!busy || i === 0} onClick={() => move(i, -1)} aria-label="Move up">
                            {busy === `move:${q.id}:up` ? <Spinner size="sm" /> : "↑"}
                          </Button>
                          <Button size="sm" color="light" disabled={!!busy || i === questions.length - 1} onClick={() => move(i, 1)} aria-label="Move down">
                            {busy === `move:${q.id}:down` ? <Spinner size="sm" /> : "↓"}
                          </Button>
                          <Button size="sm" color="secondary" outline disabled={!!busy} onClick={() => openEdit(q)}>
                            Edit
                          </Button>
                          <Button size="sm" color="danger" outline disabled={!!busy} onClick={() => removeQuestion(q, i)}>
                            {busy === `delete:${q.id}` ? <Spinner size="sm" /> : "Delete"}
                          </Button>
                        </div>
                      )}
                      {readOnly && (
                        <Button size="sm" color="secondary" outline onClick={() => openEdit(q)}>
                          View
                        </Button>
                      )}
                    </div>
                  ))}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} size="xl">
        <ModalHeader toggle={() => setModal(false)}>{editingId ? "Edit question" : "Add question"}</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg={7}>
              <FormGroup>
                <Label>Card type</Label>
                <Input type="select" value={draft.type} onChange={(e) => changeType(e.target.value)} disabled={!!editingId && readOnly}>
                  {QUESTION_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label>{draft.type === "true_false" ? "Statement" : "Question"}</Label>
                <Input
                  type="textarea"
                  rows={2}
                  value={draft.prompt}
                  onChange={(e) => setDraft((d) => ({ ...d, prompt: e.target.value }))}
                  placeholder={draft.type === "true_false" ? "print() shows text on the screen." : "Write the question"}
                />
              </FormGroup>
              <QuestionFields draft={draft} setDraft={setDraft} />
              <hr />
              <FormGroup>
                <Label>Explanation (shown after the learner answers)</Label>
                <Input
                  type="textarea"
                  rows={2}
                  value={draft.explanation}
                  onChange={(e) => setDraft((d) => ({ ...d, explanation: e.target.value }))}
                  placeholder="Why the right answer is right"
                />
              </FormGroup>
              <Row>
                <Col md={3}>
                  <FormGroup>
                    <Label>Points</Label>
                    <Input type="number" min={1} max={100} value={draft.points} onChange={(e) => setDraft((d) => ({ ...d, points: e.target.value }))} />
                  </FormGroup>
                </Col>
                <Col md={9}>
                  <Label>Lesson tag (optional)</Label>
                  <LessonTagFields key={`${editingId || "new"}-${modal}`} draft={draft} setDraft={setDraft} curricula={curricula} defaultCurriculumId={null} />
                  <small className="text-muted">When a learner misses this card, the result page sends them to this lesson.</small>
                </Col>
              </Row>
            </Col>
            <Col lg={5}>
              <Label>Preview (correct answer highlighted)</Label>
              <TestCardPreview question={previewQuestion} />
            </Col>
          </Row>
          {modalError && (
            <Alert color="danger" className="mt-3 mb-0">
              {modalError}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setModal(false)}>
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button color="primary" onClick={saveQuestion} disabled={savingQuestion}>
              {savingQuestion ? <Spinner size="sm" /> : editingId ? "Save question" : "Add question"}
            </Button>
          )}
        </ModalFooter>
      </Modal>
      <ToastContainer />
    </React.Fragment>
  )
}

export default withAuth(ManageAiTestEditor)
